import { useState } from 'react';
import { VIEWING_SLOTS, VIEWING_SLOT_LABELS, type Viewing, type ViewingDog, type ViewingStatus } from '@bbbk/schema';
import { titleCaseName } from '@bbbk/schema';
import { DateField, FilterControl, Icon, ModalFooter, PaneHeader, saveBlockers, useToast, writeWithToast, useDirtyDraft, useSavedDismiss } from '../common';
import { MasterDetailShell, useShellDismiss } from '../common/MasterDetailShell';
import { VIEWING_STATUS_OPTIONS } from '../bookings/constants';
import { api } from '../../lib/api';
import { isoDate } from '../../lib/dates';

// THE VIEWING FORM, as a pane — and the shell that logs a new one.
//
// CREATE is reached from the top-bar New menu, from whatever page you happen to
// be on: `ViewingModal`, a rail-less shell around `ViewingPane`. EDIT is a
// mode of the owner↔booking modal (Sir, 2026-09-02): the desk row, the
// attention panel and a viewing's node in any rail open `OwnerBookingModal`
// with the viewing, which shows `ViewingPane` beside the same search, household
// card and history every booking has. One modal, one rail, two forms.
//
// So the status control lives HERE and only here for `No-show`: it is the
// uncommon case, and a third control on every desk row would cost every viewing
// a decision in order to buy the rare one.
//
// A DROPDOWN, NOT CLICK-TO-CYCLE, matching BookingForm — the app chose that
// everywhere "so a status can't be toggled past the one you meant."
//
// The pane never scrolls at desktop widths: a form filled in while somebody is
// on the telephone must not hide a field below a fold. Below the phone tier the
// shell is a sheet and it does scroll, which is the app's own strategy.

interface DogDraft {
  name: string;
  breed: string;
}

export interface ViewingPaneProps {
  // Absent ⇒ create. Present ⇒ edit the viewing given.
  viewing?: Viewing;
  /** Seeds the day on a create. */
  today: string;
  /** The way out — through the shell's own exit where one is mounted. */
  onClose: () => void;
  /** A save landed — the caller revalidates. */
  onSaved: () => void;
}

export type ViewingModalProps = ViewingPaneProps;

const blankDog = (): DogDraft => ({ name: '', breed: '' });

/** The wire shape for the dogs: a blank row is KEPT, because the caller said there was a dog. */
export function draftDogsToWire(rows: DogDraft[]): ViewingDog[] {
  return rows.map((d) => ({
    name: d.name.trim() || null,
    breed: d.breed.trim() || null,
  }));
}

export function ViewingPane({ viewing, today, onClose, onSaved }: ViewingPaneProps) {
  const toast = useToast();
  // See BookingForm: the shell plays the exit, `onClose` alone does not.
  const dismiss = useShellDismiss(onClose);
  const dismissSaved = useSavedDismiss(onClose);
  const isCreate = !viewing;

  const [contactName, setContactName] = useState(viewing?.contactName ?? '');
  const [phone, setPhone] = useState(viewing?.phone ?? '');
  const [viewingOn, setViewingOn] = useState(viewing?.viewingOn ?? today);
  const [slot, setSlot] = useState(viewing?.slot ?? 'morning');
  const [status, setStatus] = useState<ViewingStatus>(viewing?.status ?? 'expected');
  const [notes, setNotes] = useState(viewing?.notes ?? '');
  const [dogs, setDogs] = useState<DogDraft[]>(
    (viewing?.dogs ?? []).map((d) => ({ name: d.name ?? '', breed: d.breed ?? '' })),
  );
  // One latch, so a double-press cannot send the create twice. It is not an
  // idempotency key and does not pretend to be — the server has none by
  // decision (ADR-5), and this only closes the window a fast second click opens.
  const [saving, setSaving] = useState(false);
  // The unsaved-changes guard's one line: the fields a save writes.
  useDirtyDraft('viewing', 'this viewing', { contactName, phone, viewingOn, slot, status, notes, dogs });
  // THE STATUS FOLLOWS THE BOOKING once one exists. A viewing that produced a
  // booking is Visited by consequence; letting the dropdown put it back to
  // Expected or No-show left a record that was Booked and Expected at once
  // (review, PR #174; Sir, 2026-09-02). The control stays, greyed, with the
  // reason on hover — and the status is not sent, so this pane is no longer a
  // second writer for it.
  const locked = !!viewing?.bookingId;
  const updDog = (i: number, patch: Partial<DogDraft>) =>
    setDogs((rows) => rows.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const removeDog = (i: number) => setDogs((rows) => rows.filter((_, j) => j !== i));

  // A NAME, A DAY AND A HALF OF IT. A phone number is offered and never
  // required (AC2), and the blocker says which of the three is missing rather
  // than greying Save out in silence.
  const blockers = saveBlockers(
    !contactName.trim() && 'A name is needed — it is what the desk row reads.',
    !viewingOn && 'Pick the day they are coming.',
  );

  const save = (dismiss: () => void) => {
    if (saving) return;
    setSaving(true);
    const body = {
      contactName: contactName.trim(),
      phone: phone.trim() || null,
      viewingOn,
      slot,
      dogs: draftDogsToWire(dogs),
      notes: notes.trim() || null,
      // Status is sent ONLY on an edit. On a create the server writes
      // 'expected' and refuses anything else, so sending it would be asking for
      // something that cannot be granted.
      ...(isCreate || locked ? {} : { status }),
    };
    void writeWithToast(toast, {
      write: () =>
        isCreate
          ? api('/viewings', { method: 'POST', body })
          : api(`/viewings/${viewing.id}`, { method: 'PATCH', body }),
      success: isCreate ? 'Viewing logged' : 'Viewing updated',
      error: isCreate ? 'Could not log the viewing.' : 'Could not save the viewing.',
      onSaved: () => {
        onSaved();
        // THROUGH THE SHELL'S OWN EXIT, not past it. At the drawer tier a raw
        // `onClose` unmounts the pane where it stands, so the viewing form tore
        // off the screen while every other form slides out (codex, PR #172).
        dismiss();
      },
      onError: () => setSaving(false), // allow a retry; the pane stays open
    });
  };

  const dogsSub = dogs.map((d) => d.name.trim() || d.breed.trim()).filter(Boolean).join(', ');

  return (
      <div className="pane anim-fade booking-pane viewing-pane">
        <div className="pane-scroll">
          <PaneHeader
            avatar={{ name: contactName || '?', id: viewing?.id ?? 'new', kind: 'person' }}
            title={contactName || 'New viewing'}
            badge={<span className="bflag-new tint-viewing">Viewing</span>}
            /* Mirrors the booking pane's owner · dogs · N stays. */
            sub={`${VIEWING_SLOT_LABELS[slot]} · ${dogsSub || 'no dogs mentioned'} · ${isCreate ? 'first enquiry' : 'enquiry'}`}
            /* NO `isNew`. PaneHeader's New badge earns its place on a booking,
               where the pane looks identical in both modes and nothing else
               says which one you are in. Here the title already reads "New
               viewing" and the tint tag already names the record, so the badge
               was a third statement of a fact stated twice — and it is the
               same mark the booking pane uses for a first-stay HOUSEHOLD,
               which a viewing does not yet have (Sir, 2026-08-27). */
            right={
              /* EDIT ONLY. On a create there is nothing to set — the status is
                 'expected' by construction, and offering a control that cannot
                 change anything is worse than offering none. */
              isCreate ? undefined : (
                <FilterControl
                  label="Status"
                  icon="eye"
                  options={VIEWING_STATUS_OPTIONS}
                  selected={[status]}
                  onChange={(keys) => setStatus((keys[0] as ViewingStatus) ?? status)}
                  mode="single"
                  clearable={false}
                  disabled={locked}
                  disabledHint="This viewing produced a booking — its status follows that."
                />
              )
            }
          />

          {/* TWO COLUMNS, and the pane therefore does not scroll (the design
              record's measured rule). Stacked, the four cards run 88px past the
              card's height at the desk width — a form filled in while somebody
              is on the telephone must not hide a field below a fold. Below
              720px `.pane-grid` collapses to one column and the pane scrolls,
              which is the app's own strategy and deliberately not overridden:
              read as absolute, the no-scroll rule would make this form unusable
              on a phone. */}
          <div className="pane-grid">
          <div className="scard">
            <div className="sh">Who</div>
            <div className="field-row">
              {/* EVERY LABEL NAMES ITS OWN CONTROL. They were bare `<label>`
                  elements beside their inputs — visible, and invisible to a
                  screen reader, which read the date field as having no name at
                  all and the others as "Optional" (codex, PR #172). */}
              <div className="field">
                <label htmlFor="viewing-contact">Name</label>
                <input
                  id="viewing-contact"
                  value={contactName}
                  placeholder="e.g. Sarah Nolan"
                  onChange={(e) => setContactName(e.target.value)}
                  onBlur={(e) => setContactName(titleCaseName(e.target.value))}
                />
              </div>
              <div className="field">
                <label htmlFor="viewing-phone">Phone</label>
                {/* Offered, never required — nothing complains when it is left
                    blank. The placeholder is the household form's own, so the
                    two phone fields read as one. */}
                <input
                  id="viewing-phone"
                  value={phone}
                  placeholder="+353 87 …"
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="scard">
            <div className="sh">When</div>
            {/* THE BOOKING FORM'S OWN DATE CARD, one cell wide, and its own AM/PM
                segment — so a viewing's day and time are picked exactly as a
                stay's are (Sir, 2026-09-02). The day was a bare <input
                type="date"> and the time said Morning/Afternoon, the one place
                in the app either did. */}
            {/* SIDE BY SIDE: the day card takes the width and the time segment
                its own, on one row (Sir, 2026-09-02) — the booking form's own
                `.ctl-label` and `.useg-sm`, in a box of the same measurements,
                since inside the pane's grid `.row3` collapses to one column. */}
            <div className="when-row">
              <DateField value={viewingOn} today={today} label="Day" onChange={setViewingOn} />
              <div>
                <span className="ctl-label" id="viewing-slot-label">Time</span>
                {/* A group, not a label: the choice is two buttons, so the
                    heading names the SET and each button says which it is. */}
                <div className="useg useg-sm" role="group" aria-labelledby="viewing-slot-label">
                  {VIEWING_SLOTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={slot === s ? 'on' : ''}
                      aria-pressed={slot === s}
                      onClick={() => setSlot(s)}
                    >
                      {VIEWING_SLOT_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="scard">
            {/* No count in the header: the rows beneath are the count, and the
                header line above the pane already says which dogs (Sir,
                2026-09-02). */}
            <div className="sh">Dogs</div>
            {/* THE SAME SHAPE AS THE HOUSEHOLD FORM'S DOG ROWS, so conversion
                copies a row rather than re-typing one. Both fields optional: a
                caller who gives neither leaves them blank, and the row is still
                kept — which is the direct inverse of OwnerForm.save, where a row
                with a breed and no name is silently discarded. */}
            {dogs.map((d, i) => (
              <div key={i} className="repeat">
                <div className="repeat-head">
                  <span className="repeat-label">{d.name.trim() || d.breed.trim() || `Dog ${i + 1}`}</span>
                  <button type="button" className="repeat-del" title="Remove dog" onClick={() => removeDog(i)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor={`viewing-dog-${i}-name`}>Name</label>
                    <input
                      id={`viewing-dog-${i}-name`}
                      value={d.name}
                      placeholder="e.g. Bracken"
                      onChange={(e) => updDog(i, { name: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor={`viewing-dog-${i}-breed`}>Breed</label>
                    <input
                      id={`viewing-dog-${i}-breed`}
                      value={d.breed}
                      placeholder="e.g. Labrador"
                      onChange={(e) => updDog(i, { breed: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="owner-contact-add" onClick={() => setDogs((r) => [...r, blankDog()])}>
              <Icon name="plus" size={13} /> Add a dog
            </button>
          </div>

          {/* NO "BOOKED" CARD. What the viewing produced is in the rail beside
              this pane — the household's history, with the stay one press away
              (AC4, AC9) — and a card saying it again here was a third statement
              of one fact (Sir, 2026-09-02). */}
          {/* THE NOTE FILLS ITS CARD. The grid stretches this card to the Dogs
              card's height beside it, and a three-line box in a tall card was
              mostly air (Sir, 2026-09-02) — the same rule the booking form's
              care card uses. */}
          <div className="scard notes-card">
            <div className="sh">Notes</div>
            <div className="field field-notes">
              <textarea value={notes} placeholder="e.g. Two weeks in August, wants to see the runs" onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          </div>
        </div>

        <ModalFooter
          onCancel={dismiss}
          onSave={() => save(dismissSaved)}
          saveLabel={isCreate ? 'Log viewing' : 'Save'}
          blockers={blockers}
        />
      </div>
  );
}

/**
 * The rail-less shell for LOGGING a viewing, from the New menu. Editing one is
 * the owner↔booking modal's business — see the note at the top.
 */
export function ViewingModal(props: ViewingModalProps) {
  return (
    <MasterDetailShell
      title={props.viewing ? props.viewing.contactName || 'Viewing' : 'New viewing'}
      modeLabel={props.viewing ? 'viewing' : 'logging an enquiry'}
      onClose={props.onClose}
      className="viewing-obx"
      /* NO RAIL. A viewing being logged has no history and no household; an
         empty rail is a blank column. */
    >
      <ViewingPane {...props} />
    </MasterDetailShell>
  );
}

/** The day a create opens on, when the caller has no opinion. */
export const defaultViewingDay = (): string => isoDate(new Date());
